import React from 'react';
import styled, { keyframes } from 'styled-components';
import { usePWA } from '../contexts/PWAContext';

const slideDown = keyframes`
  from {
    transform: translateY(-100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
`;

const IndicatorContainer = styled.div<{ show: boolean; type: 'offline' | 'online' }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: ${props => props.type === 'offline' ? '#f44336' : '#4caf50'};
  color: white;
  padding: 12px 16px;
  text-align: center;
  font-size: 14px;
  font-weight: 500;
  z-index: 1001;
  transform: translateY(${props => props.show ? '0' : '-100%'});
  transition: transform 0.3s ease-in-out;
  animation: ${props => props.show ? slideDown : 'none'} 0.3s ease-out;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
`;

const IndicatorContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  max-width: 1200px;
  margin: 0 auto;
`;

const IndicatorIcon = styled.span<{ animate?: boolean }>`
  font-size: 16px;
  animation: ${props => props.animate ? pulse : 'none'} 2s infinite;
`;

const IndicatorText = styled.span`
  flex: 1;
  text-align: center;
`;

const DismissButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 18px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  opacity: 0.8;
  transition: opacity 0.2s ease;
  min-height: 32px;
  min-width: 32px;
  
  &:hover {
    opacity: 1;
    background: rgba(255, 255, 255, 0.1);
  }
`;

const RetryButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 32px;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
  
  &:active {
    transform: translateY(1px);
  }
`;

interface OfflineIndicatorProps {
  showRetry?: boolean;
  onRetry?: () => void;
}

export function OfflineIndicator({ showRetry = false, onRetry }: OfflineIndicatorProps) {
  const { isOnline, showOfflineMessage, dismissOfflineMessage } = usePWA();

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    } else {
      window.location.reload();
    }
  };

  // Show offline message when explicitly requested or when offline
  const shouldShow = showOfflineMessage || !isOnline;
  const indicatorType = isOnline ? 'online' : 'offline';

  if (!shouldShow) {
    return null;
  }

  return (
    <IndicatorContainer show={shouldShow} type={indicatorType}>
      <IndicatorContent>
        <IndicatorIcon animate={!isOnline}>
          {isOnline ? '✅' : '📱'}
        </IndicatorIcon>
        
        <IndicatorText>
          {isOnline 
            ? 'Back online! Your data will sync automatically.'
            : 'You\'re offline. Some features may be limited.'
          }
        </IndicatorText>
        
        {!isOnline && showRetry && (
          <RetryButton onClick={handleRetry}>
            Retry
          </RetryButton>
        )}
        
        <DismissButton 
          onClick={dismissOfflineMessage}
          aria-label="Dismiss offline message"
        >
          ×
        </DismissButton>
      </IndicatorContent>
    </IndicatorContainer>
  );
}

// Network status badge for showing in the UI
const StatusBadge = styled.div<{ online: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background: ${props => props.online ? '#e8f5e8' : '#ffebee'};
  color: ${props => props.online ? '#2e7d32' : '#c62828'};
  border: 1px solid ${props => props.online ? '#c8e6c9' : '#ffcdd2'};
`;

const StatusIcon = styled.span`
  font-size: 10px;
`;

interface NetworkStatusBadgeProps {
  className?: string;
}

export function NetworkStatusBadge({ className }: NetworkStatusBadgeProps) {
  const { isOnline } = usePWA();

  return (
    <StatusBadge online={isOnline} className={className}>
      <StatusIcon>{isOnline ? '🟢' : '🔴'}</StatusIcon>
      {isOnline ? 'Online' : 'Offline'}
    </StatusBadge>
  );
}

export default OfflineIndicator;